import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  ClipboardList, 
  ArrowRight,
  CheckCircle, 
  Users, 
  TrendingUp, 
  BarChart3,
  Clock,
  Calendar,
  Target,
  Eye,
  Play,
  Award,
  UserCheck,
  DollarSign,
  Shield,
  UserPlus,
  Sparkles,
  ListTodo,
  Building2,
  GitBranch,
  Timer,
  CheckSquare,
  Activity,
  Zap
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { manageRTCTestimonials } from '../../shared/testimonialData';

const ManageRTC: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  

  const features = [
    {
      icon: Users,
      title: 'Employee Lifecycle Management',
      description: 'Complete employee management from onboarding to exit, including profiles, documents, performance tracking, and career progression.'
    },
    {
      icon: Calendar,
      title: 'Attendance & Leave Management',
      description: 'Automated attendance tracking with biometric integration, shift management, leave approvals, and real-time reporting.'
    },
    {
      icon: DollarSign,
      title: 'Payroll & Benefits Administration',
      description: 'Automated payroll processing with tax calculations, benefits management, salary advances, and direct deposit integration.'
    },
    {
      icon: ListTodo,
      title: 'Project & Task Management',
      description: 'Comprehensive project planning with task assignments, milestone tracking, dependencies, and resource allocation.'
    },
    {
      icon: BarChart3,
      title: 'Performance Analytics',
      description: 'Real-time dashboards tracking employee productivity, project progress, KPIs, and organizational metrics.'
    },
    {
      icon: Shield,
      title: 'Role-Based Access Control',
      description: 'Granular permissions with role-based access ensuring data security and compliance across HR and project modules.'
    }
  ];

  const hrFeatures = [
    {
      icon: UserPlus,
      title: 'Recruitment & Onboarding',
      description: 'Streamline hiring with applicant tracking, interview scheduling, offer management, and digital onboarding workflows.',
      features: ['Applicant Tracking System', 'Interview Scheduler', 'Offer Letter Generation', 'Digital Onboarding', 'Background Verification']
    },
    {
      icon: UserCheck,
      title: 'Employee Management',
      description: 'Centralized employee database with personal information, work history, skills, certifications, and document management.',
      features: ['Employee Profiles', 'Document Management', 'Skill Matrix', 'Certification Tracking', 'Emergency Contacts']
    },
    {
      icon: Clock,
      title: 'Time & Attendance',
      description: 'Biometric attendance, shift scheduling, overtime tracking, geofencing, and automated attendance reports.',
      features: ['Biometric Integration', 'Shift Management', 'Overtime Tracking', 'Geofencing', 'Mobile Check-in']
    },
    {
      icon: Award,
      title: 'Performance & Appraisals',
      description: 'Goal setting, continuous feedback, 360-degree reviews, competency assessments, and performance improvement plans.',
      features: ['Goal Setting', '360° Reviews', 'Competency Matrix', 'Performance Tracking', 'Appraisal Cycles']
    }
  ];

  const projectFeatures = [
    {
      icon: Target,
      title: 'Project Planning',
      description: 'Create project plans with milestones, deliverables, timelines, and resource allocation for effective project execution.',
      features: ['Project Templates', 'Milestone Tracking', 'Gantt Charts', 'Resource Planning', 'Budget Management']
    },
    {
      icon: ListTodo,
      title: 'Task Management',
      description: 'Break down projects into tasks, assign to team members, set priorities, track progress, and manage dependencies.',
      features: ['Task Assignment', 'Priority Management', 'Task Dependencies', 'Subtasks', 'Custom Workflows']
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Enable seamless team collaboration with shared workspaces, real-time updates, file sharing, and communication tools.',
      features: ['Team Workspaces', 'File Sharing', 'Comments & Mentions', 'Activity Feeds', 'Notifications']
    },
    {
      icon: BarChart3,
      title: 'Progress Tracking',
      description: 'Monitor project health with real-time progress tracking, burndown charts, resource utilization, and status reports.',
      features: ['Progress Dashboards', 'Burndown Charts', 'Time Tracking', 'Status Reports', 'Resource Utilization']
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Increased Productivity',
      description: 'Streamline HR and project workflows to boost team productivity and reduce administrative overhead.',
      metric: '45% productivity gain'
    },
    {
      icon: Timer,
      title: 'Time Savings',
      description: 'Automate repetitive tasks and approvals, saving hundreds of hours across HR and project management.',
      metric: '60% time reduction'
    },
    {
      icon: DollarSign,
      title: 'Cost Optimization',
      description: 'Reduce operational costs through automation, better resource allocation, and improved workforce planning.',
      metric: '40% cost savings'
    },
    {
      icon: Activity,
      title: 'Better Decision Making',
      description: 'Data-driven insights and real-time analytics enable informed decisions for HR and project strategies.',
      metric: '90% data accuracy'
    }
  ];

  const integrations = [
    {
      category: 'Attendance Systems',
      tools: ['Biometric Devices', 'RFID Readers', 'Face Recognition', 'Mobile GPS', 'Access Control', 'Time Clocks']
    },
    {
      category: 'Financial Systems',
      tools: ['Payroll Software', 'Banking APIs', 'Tax Systems', 'Expense Management', 'QuickBooks', 'SAP']
    },
    {
      category: 'Collaboration Tools',
      tools: ['Microsoft Teams', 'Slack', 'Google Workspace', 'Zoom', 'Email', 'Calendar']
    },
    {
      category: 'Project Tools',
      tools: ['Jira', 'Trello', 'Asana', 'GitHub', 'GitLab', 'Bitbucket']
    }
  ];

  const capabilities = [
    {
      title: 'Multi-Location Support',
      description: 'Manage employees and projects across multiple offices, branches, and remote locations with centralized control.'
    },
    {
      title: 'Mobile-First Experience',
      description: 'Native mobile apps for iOS and Android enabling employees and managers to work from anywhere, anytime.'
    },
    {
      title: 'Compliance Management',
      description: 'Built-in compliance with labor laws, tax regulations, data protection, and industry-specific requirements.'
    },
    {
      title: 'Customizable Workflows',
      description: 'Configure approval workflows, custom fields, and business rules to match your organizational processes.'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-blue-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 neon-glow">
                  <ClipboardList className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-blue-400 uppercase tracking-wide">Unified Workforce & Project Platform</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-blue-500 to-indigo-600 bg-clip-text text-transparent">ManageRTC</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">Manage Round The Clock</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
                ManageRTC is a unified digital platform that seamlessly integrates human resource management and project management into one powerful solution. From employee onboarding to project delivery, manage your entire organization's workforce, attendance, payroll, tasks, milestones, and performance—all in real-time, around the clock.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-indigo-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-300"
                >
                  Start Managing 24/7
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-500">500+</div>
                  <div className="text-sm text-muted-foreground">Organizations</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-400">50K+</div>
                  <div className="text-sm text-muted-foreground">Users</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">24/7</div>
                  <div className="text-sm text-muted-foreground">Operations</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-gradient-to-r from-blue-500 to-indigo-600 opacity-20 rounded-full blur-3xl"
                />
                
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Products/it-Products/manageRTC.png'
                    alt="ManageRTC Unified Dashboard"
                    className="w-full h-96 object-cover"
                  />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-blue-500/20 rounded-lg backdrop-blur-sm">
                          <ClipboardList className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="font-semibold">24/7 Management</p>
                          <p className="text-sm text-white/80">HR + Projects Unified</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-blue-500">
                    <Activity className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">REAL-TIME</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              <span className="brand-gradient-text">Unified HR & Project Management</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              ManageRTC brings together human resource management and project management in one integrated platform. Manage your workforce, track attendance, process payroll, assign tasks, monitor project progress, and analyze performance—all from a single, powerful dashboard that operates round the clock.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass-strong p-8 rounded-2xl hover:scale-105 transition-all duration-300"
                >
                  <div className="mb-6 p-4 rounded-xl bg-blue-500/20 w-fit">
                    <Icon className="w-10 h-10 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-justify">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ManageRTC Workflow Section */}
      <section className="relative py-20 bg-gradient-to-b from-transparent via-primary/5 to-transparent hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              <span className="brand-gradient-text">ManageRTC Workflow</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              A seamless workflow that integrates HR operations and project management, enabling your organization to operate efficiently 24/7 with automated processes and real-time insights.
            </p>
          </motion.div>

          {/* Flow Diagram */}
          <div className="mb-16">
            <div className="relative">
              {/* Desktop Flow */}
              <div className="hidden lg:flex items-center justify-between gap-4 mb-12">
                {[
                  { icon: UserPlus, title: 'Employee Onboarding', desc: 'Add employees and setup profiles', color: 'blue-500' },
                  { icon: Clock, title: 'Attendance Tracking', desc: 'Record time and attendance', color: 'indigo-500' },
                  { icon: ListTodo, title: 'Task Assignment', desc: 'Assign project tasks to team', color: 'blue-600' },
                  { icon: BarChart3, title: 'Progress Monitoring', desc: 'Track project and HR metrics', color: 'indigo-600' },
                  { icon: CheckSquare, title: 'Performance Review', desc: 'Evaluate and analyze results', color: 'blue-500' }
                ].map((step, index) => (
                  <React.Fragment key={step.title}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.5 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: index * 0.15 }}
                      viewport={{ once: true }}
                      className="flex-1 text-center"
                    >
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        className="relative mb-6 mx-auto"
                      >
                        <div className={`w-24 h-24 rounded-full bg-gradient-to-br from-${step.color}/20 to-${step.color}/10 flex items-center justify-center glass-strong border-2 border-${step.color}/30 mx-auto`}>
                          <step.icon className={`w-10 h-10 text-${step.color}`} />
                        </div>
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className={`absolute inset-0 rounded-full bg-${step.color}/10 blur-xl`}
                        />
                      </motion.div>
                      <h4 className="font-semibold text-lg mb-2">{step.title}</h4>
                      <p className="text-sm text-muted-foreground">{step.desc}</p>
                    </motion.div>
                    
                    {index < 4 && (
                      <motion.div
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        transition={{ duration: 0.5, delay: index * 0.15 + 0.3 }}
                        viewport={{ once: true }}
                        className="flex-shrink-0 self-start mt-12"
                      >
                        <div className="relative">
                          <ArrowRight className="w-8 h-8 text-primary" />
                          <motion.div
                            animate={{ x: [0, 10, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                            className="absolute inset-0"
                          >
                            <ArrowRight className="w-8 h-8 text-primary/30" />
                          </motion.div>
                        </div>
                      </motion.div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Mobile Flow */}
              <div className="lg:hidden space-y-6">
                {[
                  { icon: UserPlus, title: 'Employee Onboarding', desc: 'Add employees and setup profiles', color: 'blue-500' },
                  { icon: Clock, title: 'Attendance Tracking', desc: 'Record time and attendance', color: 'indigo-500' },
                  { icon: ListTodo, title: 'Task Assignment', desc: 'Assign project tasks to team', color: 'blue-600' },
                  { icon: BarChart3, title: 'Progress Monitoring', desc: 'Track project and HR metrics', color: 'indigo-600' },
                  { icon: CheckSquare, title: 'Performance Review', desc: 'Evaluate and analyze results', color: 'blue-500' }
                ].map((step, index) => (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="glass-strong p-6 rounded-xl"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-16 h-16 rounded-full bg-gradient-to-br from-${step.color}/20 to-${step.color}/10 flex items-center justify-center flex-shrink-0 border-2 border-${step.color}/30`}>
                        <step.icon className={`w-8 h-8 text-${step.color}`} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg mb-2">{step.title}</h4>
                        <p className="text-sm text-muted-foreground">{step.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Workflow Details */}
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="glass-strong p-8 rounded-2xl"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-xl bg-blue-500/20">
                  <Building2 className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-2xl font-semibold">HR Operations Flow</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed text-justify">
                The HR workflow includes employee onboarding, attendance management, leave approvals, payroll processing, performance reviews, and compliance tracking. All HR operations are automated with approval workflows and real-time notifications to ensure smooth workforce management around the clock.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="glass-strong p-8 rounded-2xl"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-xl bg-indigo-500/20">
                  <GitBranch className="w-8 h-8 text-indigo-500" />
                </div>
                <h3 className="text-2xl font-semibold">Project Management Flow</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed text-justify">
                The project workflow encompasses project creation, task breakdown, team assignments, progress tracking, milestone management, and delivery reporting. Projects are managed with Gantt charts, burndown charts, and real-time collaboration tools to ensure timely delivery and optimal resource utilization.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HR Features Section */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Comprehensive HR Management
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Transform your HR operations with automated workflows, digital employee records, and intelligent analytics that help you manage your workforce more effectively.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {hrFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 rounded-lg bg-blue-500/20">
                      <Icon className="w-8 h-8 text-blue-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground text-justify">{feature.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {feature.features.map((item) => (
                      <div key={item} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Project Management Features Section */}
      <section className="relative py-20 bg-gradient-to-b from-transparent via-secondary/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Advanced Project Management
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Plan, execute, and deliver projects on time with powerful project management tools that enable seamless collaboration and real-time visibility across your organization.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {projectFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 rounded-lg bg-indigo-500/20">
                      <Icon className="w-8 h-8 text-indigo-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground text-justify">{feature.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {feature.features.map((item) => (
                      <div key={item} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Measurable Business Impact
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              ManageRTC delivers tangible results with improved productivity, reduced costs, and better decision-making across your organization.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass-strong p-6 rounded-xl text-center hover:scale-105 transition-all duration-300"
                >
                  <div className="mb-4 p-4 rounded-xl bg-blue-500/20 w-fit mx-auto">
                    <Icon className="w-8 h-8 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4 text-justify">
                    {benefit.description}
                  </p>
                  <div className="text-2xl font-bold text-blue-500">
                    {benefit.metric}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="relative py-20 bg-gradient-to-b from-transparent via-accent/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Seamless Integrations
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Connect ManageRTC with your existing tools and systems for a unified workflow across your organization.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {integrations.map((integration, index) => (
              <motion.div
                key={integration.category}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass-strong p-6 rounded-xl"
              >
                <h3 className="text-lg font-semibold mb-4 text-primary">{integration.category}</h3>
                <div className="space-y-2">
                  {integration.tools.map((tool) => (
                    <div key={tool} className="flex items-center gap-2">
                      <Zap className="w-3 h-3 text-accent flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{tool}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Advanced Capabilities */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Enterprise-Grade Capabilities
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              Built for scale and security with enterprise features that grow with your organization.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="mt-1">
                    <Sparkles className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-3">{capability.title}</h3>
                    <p className="text-muted-foreground leading-relaxed text-justify">
                      {capability.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ManageRTC Testimonials Section */}
      <section className="relative py-24 bg-white/[0.02] hidden">
      <ProductTestimonials 
        testimonials={manageRTCTestimonials}
        title="Real-Time Communication Excellence"
        subtitle="See how organizations revolutionized their communication with ManageRTC"
        gradientFrom="#00CFFF"
        gradientVia="#4F46E5"
        gradientTo="#00CFFF"
        iconColor="#00CFFF"
        dotColor="bg-blue-500"
        speed={60}
        direction="left"
      />
</section>
      {/* CTA Section */}
      <section className="relative py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Ready to Manage Round The Clock?
            </h2>
            <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto text-justify">
              Transform your HR and project management with ManageRTC. Start managing your workforce and projects more efficiently today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-indigo-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-300"
              >
                Get Started Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="glass inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Schedule Demo
                <Eye className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { ManageRTC };
export default ManageRTC;